/**
 * 案件アサインのパースプレビュー表（送信前に列の割れ方と構成を確認させる）。
 *
 * 区切り（タブ/カンマ/スペース）を自動判定して列を割るため、列ズレに気づかないまま送ると
 * 別人を担当者に立ててしまう。行ごとの判定を見せて、不正な行がある限り送信させない。
 *
 * 要件定義アサインでは**「作らない成果物」の列**も持つ（決めすぎないゲート）。初期値は AI の
 * 仮判断で、案件ごとに人が直せる。**仮判断が無いこと（憲章が無い・AI が落ちた）と「全部作る」は
 * 区別して出す** — 区別しないと、判定できなかった案件が「作らないものは無いと判断された」
 * ように見える。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件アサインのパース結果と、要件定義アサインでは案件ごとの「作らない成果物」（AI 仮判断の初期値）を表で出す
 * @graph-connects RequirementScopeFields [references] 案件ごとの作らない成果物の編集
 */

import { Fragment, type ReactNode } from 'react';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import {
  ASSIGN_SKIPPABLE_ARTIFACT_LABELS,
  MAX_ASSIGN_ROWS,
  type AssignKind,
  type AssignScopeSuggestion,
  type AssignSkippedArtifact,
} from '../../../types/project-assign.js';
import type { ParsedAssignRow } from './parse-rows.js';
import { RequirementScopeFields } from './RequirementScopeFields.js';

/** @graph-connects none */
export interface AssignParsePreviewProps {
  /** パース結果（不正な行も含む）。 */
  rows: readonly ParsedAssignRow[];
  /** アサイン種別。要件定義のときだけ「作らない成果物」の列を出す。 */
  kind: AssignKind;
  /** 1 リクエストの上限を超えているか。 */
  overLimit: boolean;
  /** 案件ごとの「作らない成果物」（初期値は AI の仮判断）。 */
  scopeByKey: Readonly<Record<string, AssignSkippedArtifact[]>>;
  /** 案件ごとの AI 仮判断（判定できなかった案件の理由を出すのに使う）。 */
  suggestionByKey: Readonly<Record<string, AssignScopeSuggestion>>;
  /** 仮判断を取りに行っている最中か。 */
  suggesting: boolean;
  /** 編集を開いている案件（無ければ null）。 */
  openKey: string | null;
  /** 編集の開閉。 */
  onToggleOpen: (issueKey: string) => void;
  /** 案件 1 件の「作らない成果物」が変わった。 */
  onChangeScope: (issueKey: string, next: AssignSkippedArtifact[]) => void;
  /** 送信中は触らせない。 */
  disabled: boolean;
}

/**
 * 「作らない成果物」の列に出す中身を決める。
 *
 * 選ばれているものがあればその名前、無ければ**なぜ無いか**を出す（AI が「全部作る」と
 * 判断した / 判定できなかった / まだ判定していない を取り違えさせない）。
 * @graph-connects none
 */
export function scopeCellText(input: {
  skipped: readonly AssignSkippedArtifact[];
  suggestion: AssignScopeSuggestion | undefined;
  suggesting: boolean;
}): string {
  if (input.skipped.length > 0) {
    return input.skipped
      .map((entry) => ASSIGN_SKIPPABLE_ARTIFACT_LABELS[entry.artifact])
      .join(' / ');
  }
  if (input.suggestion !== undefined) {
    return input.suggestion.status === 'suggested'
      ? 'すべて作る（AI の仮判断）'
      : input.suggestion.note;
  }
  return input.suggesting ? 'AI が仮判断中…' : 'すべて作る';
}

/**
 * パース結果のプレビュー表。
 * @graph-connects none
 */
export function AssignParsePreview(props: AssignParsePreviewProps): ReactNode {
  // 列は 行 / 案件 / 担当エンジニア + 種別ごとの 1 列（レビュアー or 作らない成果物）。
  const COLUMNS = 4;
  const { rows, kind, overLimit, scopeByKey, suggestionByKey, suggesting, disabled } = props;
  return (
    <div className="space-y-2">
      {overLimit && (
        <p className="text-xs text-[var(--color-error)]">
          一度にアサインできるのは {MAX_ASSIGN_ROWS} 件までです。行を分けて実行してください。
        </p>
      )}
      <div className="max-h-72 overflow-auto rounded-md border border-[var(--color-border)]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-[var(--color-bg-elevated)]">
            <tr className="text-left text-[var(--color-text-muted)]">
              <th className="px-3 py-2 font-medium w-10">行</th>
              <th className="px-3 py-2 font-medium">案件</th>
              <th className="px-3 py-2 font-medium">担当エンジニア</th>
              {kind === 'development' && <th className="px-3 py-2 font-medium">レビュアー</th>}
              {kind === 'requirement' && <th className="px-3 py-2 font-medium">作らない成果物</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const skipped = scopeByKey[row.issueKey] ?? [];
              const suggestion = suggestionByKey[row.issueKey];
              const open = props.openKey === row.issueKey;
              return (
                <Fragment key={row.lineNumber}>
                  <tr className="border-t border-[var(--color-border)] align-top">
                    <td className="px-3 py-2 text-[var(--color-text-muted)]">{row.lineNumber}</td>
                    {row.error === undefined ? (
                      <>
                        <td className="px-3 py-2 font-mono">{row.issueKey}</td>
                        <td className="px-3 py-2">{row.engineerNickname}</td>
                        {kind === 'development' && (
                          <td className="px-3 py-2">{row.reviewerNickname}</td>
                        )}
                        {kind === 'requirement' && (
                          <td className="px-3 py-2">
                            <div className="flex items-start gap-2">
                              <span className="text-[var(--color-text-secondary)]">
                                {scopeCellText({ skipped, suggestion, suggesting })}
                              </span>
                              <Button
                                variant="secondary"
                                size="sm"
                                disabled={disabled}
                                aria-label={`${row.issueKey} の作らない成果物を${open ? '閉じる' : '変更する'}`}
                                onClick={() => {
                                  props.onToggleOpen(row.issueKey);
                                }}
                              >
                                {open ? '閉じる' : '変更'}
                              </Button>
                            </div>
                          </td>
                        )}
                      </>
                    ) : (
                      <td className="px-3 py-2 text-[var(--color-error)]" colSpan={COLUMNS - 1}>
                        {row.error}
                        <span className="ml-2 font-mono text-[var(--color-text-muted)]">
                          {row.raw.trim()}
                        </span>
                      </td>
                    )}
                  </tr>
                  {open && row.error === undefined && (
                    <tr className="bg-[var(--color-bg-elevated)]">
                      <td className="px-3 pb-3" colSpan={COLUMNS}>
                        <RequirementScopeFields
                          value={skipped}
                          onChange={(next) => {
                            props.onChangeScope(row.issueKey, next);
                          }}
                          disabled={disabled}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
